import { Col, Row } from 'react-bootstrap';
import ResultStats from './algoliaCustom/CustomResultStats';
import { Pagination } from 'react-instantsearch';
import { SkusDesktopProps } from '@/types/props/components';
import styles from '../../pages/skus.module.scss';
import FilterSkus from './filterSkus';
import CustomSortBy from './algoliaCustom/CustomSortBy';
import CustomHits from './algoliaCustom/CustomHits';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import seoConstants from '@/constants/seo.constants';
import { capitalizeWords } from '@/utilites/misc/misc.utils';
import Image from 'next/image';
import CategoryNav from '@/components/home/categoryNav';
import envPublic from '@/config/env.public';
import { useQueries } from '@tanstack/react-query';
import getNewSkus from '@/api/getNewSkus';
import checkArray from '@/utilites/check-array';
import strapiConstants from '@/constants/strapi.constants';
import getCollectionsUseQuery from '@/api/getCollectionsUseQuery';
import { SkuDTO } from '@/types/api/sku';
import IndexNav from '@/components/home/Index/IndexNav/IndexNav';

// Helper function for preparing query data
const prepareQueryData = <T,>(data: any): T[] => {
  return (checkArray(data) ? data : []) as T[];
};

// Shared React Query config to reduce repetition
const QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
};

export default function SkusDesktop({
  allSortsCustomTranslated,
  tCommon,
  push,
  user,
  wishlistContext,
  windowWidth,
  h1Title,
  brandsContent,
  includeCategoryNav,
}: SkusDesktopProps) {
  const router = useRouter();
  const { query, locale } = router;
  const [stateMeta, setStateMeta] = useState<string | null>(null);

  const selectSkus = useCallback((data: any) => prepareQueryData<SkuDTO>(data), []);

  const [collectionsQuery, newSkusQuery] = useQueries({
    queries: [
      {
        ...QUERY_CONFIG,
        queryKey: ['collections', strapiConstants.queries.collection, locale],
        queryFn: getCollectionsUseQuery,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['newSkus'],
        queryFn: getNewSkus,
        select: selectSkus,
      },
    ],
  });

  const newCollections = useMemo(() => {
    const collections = collectionsQuery.data || [];
    const _newCollections = collections.sort((a, b) => {
      const dateA = new Date(a.attributes?.createdAt || '');
      const dateB = new Date(b.attributes?.createdAt || '');
      return dateB.getTime() - dateA.getTime();
    });
    const newCollections = _newCollections.slice(0, 5);
    return newCollections.map(collection => ({
      name: collection?.attributes?.name || '',
      href: `/collections/${collection.attributes?.slug}`,
    }));
  }, [collectionsQuery.data]);

  const newSkus = useMemo(() => {
    const skus = newSkusQuery.data || [];
    const _newSkus = skus.slice(0, 5).map(sku => ({
      name: sku?.name || '',
      href: `/${sku.slug}`,
    }));
    return _newSkus;
  }, [newSkusQuery.data]);

  useEffect(() => {
    const metaTypeValues = Object.values(query) || [];
    const metaType: string[] = [];
    metaTypeValues
      .filter(value => value !== undefined)
      .forEach((value: string | string[]) => {
        if (Array.isArray(value)) {
          metaType.push(...value.map(v => v?.toLowerCase()));
          return;
        }
        metaType.push(value?.toLowerCase());
      });

    let h1Title = 'products';

    const includesWhisky = metaType.includes(seoConstants.metaTypeParams.whisky);

    if (metaType.includes(seoConstants.metaTypeParams.rum)) {
      h1Title = 'rum';
    } else if (metaType.includes(seoConstants.metaTypeParams.gin)) {
      h1Title = 'gin';
    } else if (metaType.includes(seoConstants.metaTypeParams.other)) {
      h1Title = 'other';
    } else if (
      metaType.find(value => {
        return seoConstants.metaTypeParams.whiskyName
          .map(whiskyValue => whiskyValue?.toLowerCase())
          .includes(value);
      })
    ) {
      const whiskyName = metaType.find(value => {
        return seoConstants.metaTypeParams.whiskyName
          .map(whiskyValue => whiskyValue?.toLowerCase())
          .includes(value);
      })!;
      h1Title = 'Whisky - ' + capitalizeWords(whiskyName);
    } else if (metaType.includes(envPublic.NEXT_PUBLIC_ALGOLIA_INDEX?.toLowerCase())) {
      h1Title = 'products';
    } else if (metaType.includes(envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_BY_WATCHED?.toLowerCase())) {
      h1Title = 'products';
    } else if (includesWhisky && metaType.includes('scotland')) {
      h1Title = 'meta_scotland_whisky_title';
    } else if (includesWhisky && metaType.includes('usa')) {
      h1Title = 'meta_usa_whisky_title';
    } else if (includesWhisky && metaType.includes('japan')) {
      h1Title = 'meta_japan_whisky_title';
    } else if (includesWhisky && metaType.includes('ireland')) {
      h1Title = 'meta_ireland_whisky_title';
    } else if (includesWhisky && metaType.includes('germany')) {
      h1Title = 'meta_germany_whisky_title';
    } else if (includesWhisky && metaType.includes('taiwan')) {
      h1Title = 'meta_taiwan_whisky_title';
    } else if (includesWhisky) {
      h1Title = 'Whisky';
    }
    setStateMeta(h1Title);
  }, [query, tCommon]);
  return (
    <>
      {includeCategoryNav && (
        <>
          <Col xs={12} className="max-nine:!tw-hidden">
            <IndexNav newSkus={newSkus} newCollections={newCollections} />
          </Col>
          <Col xs={12} className={`tw-mb-5 nine:!tw-hidden`}>
            <CategoryNav userId={null} />
          </Col>
        </>
      )}
      {brandsContent && brandsContent.banner_image ? (
        <div>
          <div className={`tw-relative`}>
            <Image
              key={brandsContent.banner_image[0].id}
              src={`${brandsContent.banner_image[0].url}`}
              alt={brandsContent.name || ''}
              width={1325}
              height={285}
              className={`tw-box-content tw-max-w-full tw-rounded-[25px]`}
            ></Image>
            <div
              className={`tw-absolute tw-left-[50px] tw-top-0 tw-max-w-[650px] tw-translate-y-1/2 tw-text-white`}
            >
              <h1 className={`tw-font-prata tw-font-normal`}>{brandsContent.banner_headline}</h1>
              <hr
                className={`tw-w-[350px] tw-rounded-[50px] tw-border-[4px] tw-border-custom-gold tw-opacity-100`}
              ></hr>
              <h2 className={`tw-text-[18px] tw-font-medium tw-leading-[33px] tw-text-[#DEDFE2]`}>
                {brandsContent.banner_text}
              </h2>
            </div>
          </div>

          <div className={`tw-align-center tw-my-10 tw-flex tw-justify-between tw-gap-[15px]`}>
            <a
              href={`/brands/Macallan`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/macallan_logo.webp`}
                alt={`Macallan Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
            <a
              href={`/brands/Ardbeg`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/ardbeg_logo%20copia.webp`}
                alt={`Ardbeg Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
            <a
              href={`/brands/Johnnie-Walker`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/johnnie-walker-logo.webp`}
                alt={`Johnnie Walker Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
            <a
              href={`/brands/Lagavulin`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/lagavulin_logo.webp`}
                alt={`Lagavulin Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
            <a
              href={`/brands/Laphroaig`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/Laphroaig_logo.webp`}
                alt={`Laphroiag Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
            <a
              href={`/brands/Springbank`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/springbanck-logo.webp`}
                alt={`Sprinkbank Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
            <a
              href={`/brands/Yamazaki`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/yamazaki_logo.webp`}
                alt={`Yamazaki Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
            <a
              href={`/brands/Glenfiddich`}
              title={``}
              className={`tw-flex-1 tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-custom-gray tw-shadow-brands`}
            >
              <Image
                src={`https://spiritory2storage.blob.core.windows.net/assets/images/brands/logos/glenffidich_logo.webp`}
                alt={`Glenfiddich Logo`}
                width={140}
                height={75}
                className={`tw-w-full tw-rounded-[8px]`}
              ></Image>
            </a>
          </div>
        </div>
      ) : null}
      <Col lg="3" className={styles.hidePhone}>
        <FilterSkus />
      </Col>
      <Col lg="9">
        <Row className={`d-flex align-items-center py-3 ${styles.hideMobile}`}>
          <Col className={styles.hideMobile}>
            {brandsContent && brandsContent.banner_image ? (
              <h2 className={`${styles.hideMobile} tw-text-[18px] tw-font-semibold`}>
                {stateMeta ? tCommon(stateMeta) : tCommon(h1Title)} <ResultStats />
              </h2>
            ) : (
              <h1
                className={`${styles.hideMobile} s-title fw-bold border-bottom border-dark border-3 pb-2 d-table mb-5`}
              >
                {stateMeta ? tCommon(stateMeta) : tCommon(h1Title)}
              </h1>
            )}
          </Col>
          <Col className={`d-flex justify-content-end ${styles.hideMobile}`}>
            <CustomSortBy items={allSortsCustomTranslated} />
          </Col>
        </Row>
        <CustomHits
          tCommon={tCommon}
          user={user}
          wishlistContext={wishlistContext}
          push={push}
          windowWidth={windowWidth}
        />
        <Row className="justify-content-center mb-5">
          <Col className="col-auto">
            <Pagination onClick={() => window.scrollTo({ top: 0 })} />
          </Col>
        </Row>
      </Col>
    </>
  );
}
